import { tldFromHostname } from "src/configs/meta-pixel";

/**
 * Where the header's logo / "home" link should send a customer.
 *
 * This used to be derived from the HTTP referer:
 *
 *     const homeLink = getHomeFromReferer(referer)   // referer = "https://fotodelavnica.si" by default
 *
 * and **no call site ever passed a referer**, so the default won every time.
 * The result was that every storefront — Croatian, Slovak, Polish, Czech,
 * Austrian — sent every customer to the Slovenian marketing site when they
 * clicked the logo, 100% of the time. The referer branching was unreachable.
 *
 * The storefront a customer is *on* is the only correct signal, and it is the
 * same one `DOMAIN_MAP_TO_CURRENCY` and the Meta pixel resolution already use.
 * Keyed by TLD via the shared `tldFromHostname`, so a stage host
 * (`narudzba.stage.fotoradnja.hr`) resolves to the same brand as production.
 */

export const DEFAULT_MARKETING_HOME = "https://fotodelavnica.si";

/** TLD → marketing apex. Mirrors the storefronts in DOMAIN_MAP_TO_CURRENCY. */
const MARKETING_HOME_BY_TLD: Record<string, string> = {
  si: "https://fotodelavnica.si",
  hr: "https://fotoradnja.hr",
  sk: "https://fotospomienky.sk",
  cz: "https://fotkavzpominek.cz",
  at: "https://fotokreation.at",
  pl: "https://foto-chwila.pl",
};

/**
 * Falls back to the Slovenian site for localhost, bare IPs and anything
 * unmapped — the previous behaviour, so an unknown host is never worse off
 * than it was, and never yields a broken link.
 */
export function marketingHomeUrl(
  hostname: string | null | undefined,
): string {
  return MARKETING_HOME_BY_TLD[tldFromHostname(hostname)] ?? DEFAULT_MARKETING_HOME;
}

/**
 * The locale a storefront's own domain implies, or null when the host does not
 * map to one (localhost, an unmapped TLD, or a bare IP).
 *
 * Used by the header's auto-locale redirect, which had the same defect as the
 * home link: it keyed off a `referer` prop nothing passed, so it never fired.
 * Note `.it` maps to Italian even though there is no Italian storefront in
 * DOMAIN_MAP_TO_CURRENCY — preserved from the original branching rather than
 * quietly dropped.
 */
const LOCALE_BY_TLD: Record<string, string> = {
  hr: "hr",
  sk: "sk",
  cz: "cs",
  at: "de-AT",
  it: "it",
  pl: "pl",
  // Deliberately NO `si`: the original branching had none, so a first-time
  // visitor on the Slovenian domain viewing /en was never redirected. Adding it
  // would introduce a redirect that has never existed.
};

export function storefrontLocaleForHost(
  hostname: string | null | undefined,
): string | null {
  return LOCALE_BY_TLD[tldFromHostname(hostname)] ?? null;
}
